import { useState, useEffect, useRef } from 'react';
import { Search, Loader2 } from 'lucide-react';
import api from '../api';

const MIN_CHARS = 2;
const MAX_RESULTS = 30;
const DEBOUNCE_MS = 250;

// Busca cliente cadastrado no servidor enquanto o usuário digita (nome, telefone, CPF/CNPJ ou e-mail).
// Não baixa a lista inteira: cada busca traz no máximo 30 resultados.
export default function ClienteSearch({ onSelect, placeholder = 'Digite o nome, telefone ou CPF do cliente...', testId = 'cliente-search', autoFocus = false }) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const reqId = useRef(0);

  const q = term.trim();
  const canSearch = q.length >= MIN_CHARS;

  useEffect(() => {
    if (!canSearch) { setResults([]); setTotal(0); setLoading(false); setError(false); return undefined; }
    setLoading(true);
    const my = ++reqId.current;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/clientes', { params: { search: q, limit: MAX_RESULTS }, signal: controller.signal });
        if (my !== reqId.current) return; // resposta de uma digitação anterior
        setResults(res.data);
        setTotal(Number(res.headers['x-total-count']) || res.data.length);
        setError(false);
      } catch (err) {
        if (my !== reqId.current || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        setError(true); setResults([]); setTotal(0);
      } finally {
        if (my === reqId.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [q, canSearch]);

  // Fecha ao clicar fora
  useEffect(() => {
    const handle = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const handleSelect = (c) => {
    setTerm(''); setOpen(false); setResults([]);
    onSelect(c);
  };

  // position:fixed para a lista não ser cortada pelo overflow do modal
  const dropdownStyle = () => {
    if (!inputRef.current) return {};
    const rect = inputRef.current.getBoundingClientRect();
    return { top: rect.bottom + 2, left: rect.left, width: rect.width };
  };

  return (
    <div ref={containerRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          value={term}
          onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
          placeholder={placeholder}
          autoComplete="off"
          autoFocus={autoFocus}
          data-testid={testId}
          className="w-full pl-9 pr-9 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
      </div>

      {open && (
        <div className="fixed z-[9999] bg-white border border-slate-200 rounded-xl shadow-2xl max-h-56 overflow-y-auto" style={dropdownStyle()} data-testid={`${testId}-results`}>
          {!canSearch ? (
            <p key="dica" className="px-4 py-3 text-sm text-slate-400 text-center">{`Digite pelo menos ${MIN_CHARS} letras ou números`}</p>
          ) : error ? (
            <p key="erro" className="px-4 py-3 text-sm text-red-500 text-center">Não foi possível buscar agora. Tente de novo.</p>
          ) : loading && results.length === 0 ? (
            <p key="buscando" className="px-4 py-3 text-sm text-slate-400 text-center">Buscando...</p>
          ) : results.length === 0 ? (
            <p key="vazio" className="px-4 py-3 text-sm text-slate-400 text-center">{`Nenhum cliente encontrado para "${q}"`}</p>
          ) : (
            <div key="lista">
              {results.map((c) => (
                <button key={c.id} type="button" onMouseDown={() => handleSelect(c)}
                  data-testid={`${testId}-item-${c.id}`}
                  className="w-full text-left px-4 py-2.5 hover:bg-orange-50 transition-colors border-b border-slate-100 last:border-0 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-slate-500">
                    {(c.nome || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{c.nome}</p>
                    <p className="text-xs text-slate-500 truncate">{[c.telefone, c.documento].filter(Boolean).join(' · ') || 'Sem telefone/documento'}</p>
                  </div>
                </button>
              ))}
              {total > results.length && (
                <p className="px-4 py-2 text-xs text-slate-400 bg-slate-50 text-center border-t border-slate-100">{`Mostrando ${results.length} de ${total} — digite mais para refinar`}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
